import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import { FlaskConical, FolderOpen, BarChart3, RotateCcw, Download, Plus, Trophy, Radio, Calendar, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { AccountBlock } from './AccountBlock';
import { ColumnMapper } from './ColumnMapper';
import { CIQDashboard, SavedWinner } from './CIQDashboard';
import { campaignIntelligenceApi } from '../../lib/api';
import {
  AccountData, GroupedRow, BuildStats, SignalType, ColumnMapping, FieldDef,
  META_FIELDS, RT_FIELDS, DEMO_META, DEMO_RT,
  readXLS, autoDetect, applyMapping, buildDataset, parseNum, exportCSV, fmt$, fmtR,
} from '../../lib/campaignIntelligence';

type View = 'import' | 'dashboard' | 'saved';

const STORAGE_KEY_WINNERS = 'ciq-winners';
const STORAGE_KEY_ACCOUNTS = 'ciq-saved-accounts';

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

function saveToStorage(key: string, value: any) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

export function CampaignIntelligenceTab() {
  // ── STATE ──
  const [view, setView] = useState<View>('import');
  const [accounts, setAccounts] = useState<AccountData[]>(() => {
    const savedNames = loadFromStorage<string[]>(STORAGE_KEY_ACCOUNTS, []);
    return [{
      id: 1, name: savedNames[0] || 'Account 1',
      metaFile: null, rtFile: null, metaRaw: null, rtRaw: null,
      metaRows: null, rtRows: null, metaMapping: null, rtMapping: null,
    }];
  });
  const [nextId, setNextId] = useState(2);
  const [allRows, setAllRows] = useState<GroupedRow[]>([]);
  const [stats, setStats] = useState<BuildStats | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [activeSignal, setActiveSignal] = useState<SignalType | null>(null);
  const [savedWinners, setSavedWinners] = useState<SavedWinner[]>(() => loadFromStorage(STORAGE_KEY_WINNERS, []));

  // Column mapper state
  const [mapperOpen, setMapperOpen] = useState(false);
  const [mapperType, setMapperType] = useState<'meta' | 'rt'>('meta');
  const [mapperHeaders, setMapperHeaders] = useState<string[]>([]);
  const [mapperFields, setMapperFields] = useState<FieldDef[]>([]);
  const [mapperMapping, setMapperMapping] = useState<ColumnMapping>({});
  const [mapperCallback, setMapperCallback] = useState<((mapping: ColumnMapping) => void) | null>(null);

  // Date range state
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // RedTrack API state
  const [rtApiConfigured, setRtApiConfigured] = useState<boolean | null>(null);
  const [rtApiFetching, setRtApiFetching] = useState(false);
  const [rtApiError, setRtApiError] = useState('');
  const [rtApiResult, setRtApiResult] = useState<{ rows: number; dateRange: string } | null>(null);

  // Check RedTrack API status on mount
  useEffect(() => {
    campaignIntelligenceApi.getRedTrackStatus()
      .then(res => setRtApiConfigured(res.data?.data?.configured ?? false))
      .catch(() => setRtApiConfigured(false));
  }, []);

  // ── HELPERS ──
  const readyToProcess = useCallback(() => {
    return accounts.some(a => a.metaRows && a.rtRows && a.metaRows.length > 0 && a.rtRows.length > 0);
  }, [accounts]);

  const persistWinners = useCallback((rows: GroupedRow[]) => {
    const winners = rows.filter(r => r.signal === 'win');
    const existing = loadFromStorage<SavedWinner[]>(STORAGE_KEY_WINNERS, []);
    const map: Record<string, SavedWinner> = {};
    existing.forEach(w => { if (w?.key) map[w.key] = w; });
    winners.forEach(r => {
      const key = r.account + '|||' + r.creative + '|||' + r.page;
      map[key] = {
        key, account: r.account, creative: r.creative, page: r.page,
        roi: r.roi, spend: r.spend, revenue: r.revenue, profit: r.profit,
        signal: r.signal || 'win',
        firstSeen: map[key]?.firstSeen || new Date().toISOString(),
        lastSeen: new Date().toISOString(),
      };
    });
    const list = Object.values(map).sort((a, b) => (b.roi || 0) - (a.roi || 0));
    setSavedWinners(list);
    saveToStorage(STORAGE_KEY_WINNERS, list);
  }, []);

  // ── ACCOUNT MANAGEMENT ─��
  const addAccount = () => {
    const savedNames = loadFromStorage<string[]>(STORAGE_KEY_ACCOUNTS, []);
    const name = savedNames[accounts.length] || `Account ${nextId}`;
    setAccounts(prev => [...prev, {
      id: nextId, name,
      metaFile: null, rtFile: null, metaRaw: null, rtRaw: null,
      metaRows: null, rtRows: null, metaMapping: null, rtMapping: null,
    }]);
    setNextId(n => n + 1);
  };

  const removeAccount = (id: number) => {
    if (accounts.length <= 1) return;
    setAccounts(prev => prev.filter(a => a.id !== id));
  };

  const renameAccount = (id: number, name: string) => {
    setAccounts(prev => prev.map(a => a.id === id ? { ...a, name } : a));
  };

  // Save account names whenever they change
  useEffect(() => {
    saveToStorage(STORAGE_KEY_ACCOUNTS, accounts.map(a => a.name));
  }, [accounts]);

  // ── FILE LOADING ──
  const handleFileLoad = async (acctId: number, type: 'meta' | 'rt', file: File) => {
    if (mapperOpen) {
      alert('Please finish the current column mapping before uploading another file.');
      return;
    }
    try {
      const rows = await readXLS(file);
      if (!rows?.length) { alert('File appears empty or could not be parsed.'); return; }
      const headers = Object.keys(rows[0]);
      if (!headers.length) { alert('File has no columns — please check the format.'); return; }

      const fields = type === 'meta' ? META_FIELDS : RT_FIELDS;
      const autoMap = autoDetect(headers, fields);

      // Open mapper
      setMapperType(type);
      setMapperHeaders(headers);
      setMapperFields(fields);
      setMapperMapping(autoMap);
      setMapperCallback(() => (mapping: ColumnMapping) => {
        const mapped = applyMapping(rows, mapping);
        setAccounts(prev => prev.map(a => {
          if (a.id !== acctId) return a;
          if (type === 'meta') {
            return { ...a, metaRaw: rows, metaRows: mapped, metaMapping: mapping, metaFile: file.name };
          } else {
            return { ...a, rtRaw: rows, rtRows: mapped, rtMapping: mapping, rtFile: file.name };
          }
        }));
      });
      setMapperOpen(true);
    } catch (err: any) {
      alert('Could not read file: ' + err.message);
    }
  };

  const handleRemap = (acctId: number, type: 'meta' | 'rt') => {
    if (mapperOpen) { alert('Please finish the current column mapping first.'); return; }
    const acct = accounts.find(a => a.id === acctId);
    if (!acct) return;
    const raw = type === 'meta' ? acct.metaRaw : acct.rtRaw;
    if (!raw?.length) return;
    const headers = Object.keys(raw[0]);
    const fields = type === 'meta' ? META_FIELDS : RT_FIELDS;
    const current = type === 'meta' ? acct.metaMapping : acct.rtMapping;

    setMapperType(type);
    setMapperHeaders(headers);
    setMapperFields(fields);
    setMapperMapping(current || autoDetect(headers, fields));
    setMapperCallback(() => (mapping: ColumnMapping) => {
      const mapped = applyMapping(raw, mapping);
      setAccounts(prev => prev.map(a => {
        if (a.id !== acctId) return a;
        if (type === 'meta') return { ...a, metaRows: mapped, metaMapping: mapping };
        else return { ...a, rtRows: mapped, rtMapping: mapping };
      }));
    });
    setMapperOpen(true);
  };

  // ── MAP WITH REDTRACK API ──
  const mapWithRedTrack = async () => {
    // Check that at least one account has Meta data loaded
    const acctWithMeta = accounts.filter(a => a.metaRows && a.metaRows.length > 0);
    if (!acctWithMeta.length) {
      alert('Upload a Facebook/Meta file first, then click "Map with RedTrack Data".');
      return;
    }

    setRtApiFetching(true);
    setRtApiError('');
    setRtApiResult(null);

    try {
      const resp = await campaignIntelligenceApi.getRedTrackData({ date_from: dateFrom, date_to: dateTo });
      const data = resp.data?.data;

      if (!data?.rows?.length) {
        setRtApiError(`No data returned from RedTrack for ${dateFrom} to ${dateTo}.`);
        setRtApiFetching(false);
        return;
      }

      // Apply RT data to all accounts that have Meta data but no RT data
      // (or overwrite existing RT data if user explicitly clicked this)
      setAccounts(prev => prev.map(a => {
        if (!a.metaRows || a.metaRows.length === 0) return a;
        return {
          ...a,
          rtRows: data.rows,
          rtRaw: data.rows,
          rtFile: `RedTrack API (${dateFrom} → ${dateTo})`,
          rtMapping: { rtcampaign: 'rtcampaign', revenue: 'revenue', conversions: 'conversions', lpClicks: 'lpClicks', opEpc: 'opEpc' },
        };
      }));

      setRtApiResult({ rows: data.totalNormalized, dateRange: `${dateFrom} to ${dateTo}` });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to fetch RedTrack data';
      setRtApiError(msg);
    } finally {
      setRtApiFetching(false);
    }
  };

  // ── PROCESS ──
  const process = () => {
    const readyAccts = accounts.filter(a => a.metaRows && a.rtRows && a.metaRows.length > 0 && a.rtRows.length > 0);
    if (!readyAccts.length) {
      alert('No accounts are ready. Upload both Meta and RedTrack files for at least one account.');
      return;
    }
    try {
      const result = buildDataset(readyAccts);
      setAllRows(result.rows);
      setStats(result.stats);
      setActiveSignal(null);
      setIsDemo(false);
      setView('dashboard');
      persistWinners(result.rows);
    } catch (e: any) {
      console.error('CIQ process error:', e);
      alert('Error building dashboard: ' + e.message);
    }
  };

  // ── DEMO ──
  const loadDemo = () => {
    setMapperOpen(false);
    const demoAcct: AccountData = {
      id: 1, name: 'Demo Account',
      metaRaw: null, rtRaw: null, metaMapping: null, rtMapping: null,
      metaFile: 'demo', rtFile: 'demo',
      metaRows: DEMO_META as any, rtRows: DEMO_RT.map(r => ({ ...r, rtcampaign: r.campaign })) as any,
    };
    setAccounts([demoAcct]);
    setNextId(2);
    setIsDemo(true);

    const result = buildDataset([demoAcct]);
    setAllRows(result.rows);
    setStats(result.stats);
    setActiveSignal(null);
    setView('dashboard');
    persistWinners(result.rows);
  };

  // ── RESET ──
  const resetSession = () => {
    if (!window.confirm('Clear all loaded files and start a new session?\nSaved winners and account names are kept.')) return;
    setMapperOpen(false);
    const savedNames = loadFromStorage<string[]>(STORAGE_KEY_ACCOUNTS, []);
    setAccounts([{
      id: 1, name: savedNames[0] || 'Account 1',
      metaFile: null, rtFile: null, metaRaw: null, rtRaw: null,
      metaRows: null, rtRows: null, metaMapping: null, rtMapping: null,
    }]);
    setNextId(2);
    setAllRows([]);
    setStats(null);
    setActiveSignal(null);
    setIsDemo(false);
    setView('import');
  };

  // ── CLEAR SAVED WINNERS ──
  const clearSavedWinners = () => {
    if (!window.confirm('Clear all saved winners? This cannot be undone.')) return;
    setSavedWinners([]);
    saveToStorage(STORAGE_KEY_WINNERS, []);
  };

  // ── RENDER ──
  return (
    <div>
      {/* Sub-navigation */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <SubNavBtn active={view === 'import'} onClick={() => setView('import')} icon={<FolderOpen size={14} />} label="Import Data" />
        <SubNavBtn active={view === 'dashboard'} onClick={() => setView('dashboard')} icon={<BarChart3 size={14} />} label="Dashboard" disabled={allRows.length === 0} />
        <SubNavBtn active={view === 'saved'} onClick={() => setView('saved')} icon={<Trophy size={14} />} label={`Saved Winners (${savedWinners.length})`} />
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={resetSession}>
          <RotateCcw size={13} className="mr-1" /> New Session
        </Button>
        <Button variant="outline" size="sm" onClick={loadDemo}>
          <FlaskConical size={13} className="mr-1" /> Load Demo
        </Button>
        {view === 'import' && (
          <Button onClick={process} disabled={!readyToProcess()} size="sm">
            Process & Build Dashboard →
          </Button>
        )}
      </div>

      {/* ── IMPORT VIEW ─�� */}
      {view === 'import' && (
        <div className="max-w-[1040px]">
          {/* Naming convention */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Campaign Naming Convention</div>
            <div className="font-mono text-sm text-primary bg-muted rounded-md px-3 py-2 mb-3">
              CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp1
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { seg: 'CT28', lbl: 'Group', color: 'blue' },
                { seg: 'CL-BROLL-RT-1-101', lbl: 'Creative', color: 'cyan' },
                { seg: 'Noah Smith', lbl: 'Page', color: 'green' },
                { seg: 'ABO-camp1', lbl: 'Version', color: 'purple' },
              ].map(p => (
                <div key={p.lbl} className="flex flex-col items-center gap-1">
                  <span className={`font-mono text-[10px] px-2 py-0.5 rounded border ${
                    p.color === 'blue' ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800' :
                    p.color === 'cyan' ? 'bg-cyan-50 dark:bg-cyan-950/30 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800' :
                    p.color === 'green' ? 'bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800' :
                    'bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800'
                  }`}>{p.seg}</span>
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{p.lbl}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              The page name is detected automatically — it's the part with a space (e.g. <span className="text-green-600 dark:text-green-400 font-mono">Noah Smith</span>). Group is first, creative is everything between, version is everything after.
            </p>
          </div>

          {/* Account blocks */}
          <div className="space-y-3 mb-4">
            {accounts.map(acct => (
              <AccountBlock
                key={acct.id}
                account={acct}
                canRemove={accounts.length > 1}
                onRename={renameAccount}
                onRemove={removeAccount}
                onFileLoad={handleFileLoad}
                onRemap={handleRemap}
              />
            ))}
          </div>

          <button
            onClick={addAccount}
            className="w-full py-2.5 rounded-lg border border-dashed border-border text-muted-foreground text-xs font-medium hover:border-green-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/20 transition-colors mb-4"
          >
            <Plus size={14} className="inline mr-1" /> Add Another Account
          </button>

          {/* ── RedTrack API Section ── */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Radio size={14} className="text-red-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-foreground">RedTrack API Integration</span>
              {rtApiConfigured === true && (
                <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                  <CheckCircle size={10} /> API Key Configured
                </span>
              )}
              {rtApiConfigured === false && (
                <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
                  <XCircle size={10} /> API Key Not Set
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">
              Upload your Facebook file above, select a date range, then click <b>"Map with RedTrack Data"</b> to auto-fetch revenue data from RedTrack's API.
              Or upload a RedTrack file manually above — both methods work.
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Calendar size={13} className="text-muted-foreground" />
                <label className="text-[10px] font-semibold text-muted-foreground uppercase">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none focus:border-primary"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] font-semibold text-muted-foreground uppercase">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none focus:border-primary"
                />
              </div>
              <Button
                onClick={mapWithRedTrack}
                disabled={rtApiFetching || rtApiConfigured === false || !accounts.some(a => a.metaRows && a.metaRows.length > 0)}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {rtApiFetching ? (
                  <><RefreshCw size={13} className="mr-1.5 animate-spin" /> Fetching RedTrack…</>
                ) : (
                  <><Radio size={13} className="mr-1.5" /> Map with RedTrack Data</>
                )}
              </Button>
            </div>

            {/* Status messages */}
            {rtApiError && (
              <div className="mt-2 px-3 py-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 text-xs">
                {rtApiError}
              </div>
            )}
            {rtApiResult && (
              <div className="mt-2 px-3 py-2 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 text-xs">
                ✓ Fetched <b>{rtApiResult.rows}</b> campaigns from RedTrack ({rtApiResult.dateRange}). RedTrack data applied to all accounts with Meta files. Click <b>"Process & Build Dashboard"</b> to see results.
              </div>
            )}

            {rtApiConfigured === false && (
              <div className="mt-2 px-3 py-2 rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 text-xs">
                Add <code className="font-mono bg-yellow-100 dark:bg-yellow-900/50 px-1 rounded">REDTRACK_API_KEY</code> to your server's environment variables to enable this feature. You can still upload RedTrack files manually above.
              </div>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <Button onClick={process} disabled={!readyToProcess()} className="flex-1">
              Process & Build Dashboard →
            </Button>
            <Button variant="outline" onClick={loadDemo}>
              <FlaskConical size={14} className="mr-1.5" /> Load Demo
            </Button>
          </div>
        </div>
      )}

      {/* ── DASHBOARD VIEW ���─ */}
      {view === 'dashboard' && allRows.length > 0 && (
        <CIQDashboard
          allRows={allRows}
          stats={stats}
          isDemo={isDemo}
          savedWinners={savedWinners}
          onSignalFilter={setActiveSignal}
          activeSignal={activeSignal}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      )}

      {view === 'dashboard' && allRows.length === 0 && (
        <div className="text-center py-16">
          <BarChart3 size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-base font-semibold text-muted-foreground mb-2">No data loaded yet</h3>
          <p className="text-xs text-muted-foreground mb-4">Import your Meta Ads and RedTrack files, or load the demo to explore.</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={loadDemo}><FlaskConical size={14} className="mr-1.5" /> Load Demo</Button>
            <Button variant="outline" onClick={() => setView('import')}><FolderOpen size={14} className="mr-1.5" /> Import Files</Button>
          </div>
        </div>
      )}

      {/* ─��� SAVED WINNERS VIEW ── */}
      {view === 'saved' && (
        <div className="max-w-[1040px]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold">Saved Winners</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Winning creatives remembered across sessions. Updated every time you build the dashboard.</p>
            </div>
            {savedWinners.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearSavedWinners} className="text-red-500 hover:text-red-600">
                Clear All
              </Button>
            )}
          </div>

          {savedWinners.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-lg">
              <Trophy size={44} className="mx-auto text-muted-foreground mb-3" />
              <h3 className="text-base font-semibold text-muted-foreground mb-2">No saved winners yet</h3>
              <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                Build the dashboard once — any creatives with ROI ≥ 50% and Spend ≥ $500 get saved here automatically.
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Account</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Creative</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Page</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">ROI</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Spend</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Revenue</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Profit</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">First Seen</th>
                      <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedWinners.map((w) => (
                      <tr key={w.key} className="border-t border-border/30 hover:bg-muted/30">
                        <td className="px-3 py-2 text-muted-foreground text-[11px]">{w.account}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-primary">{w.creative}</td>
                        <td className="px-3 py-2 font-semibold">{w.page}</td>
                        <td className="px-3 py-2 font-mono text-green-600 dark:text-green-400">{fmtR(w.roi)}</td>
                        <td className="px-3 py-2 font-mono">{fmt$(w.spend)}</td>
                        <td className="px-3 py-2 font-mono">{fmt$(w.revenue)}</td>
                        <td className="px-3 py-2 font-mono text-green-600 dark:text-green-400">{fmt$(w.profit)}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                          {new Date(w.firstSeen).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                          {new Date(w.lastSeen).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Column Mapper Modal */}
      <ColumnMapper
        isOpen={mapperOpen}
        type={mapperType}
        headers={mapperHeaders}
        fields={mapperFields}
        initialMapping={mapperMapping}
        onConfirm={(mapping) => {
          if (mapperCallback) mapperCallback(mapping);
          setMapperOpen(false);
          setMapperCallback(null);
        }}
        onCancel={() => {
          setMapperOpen(false);
          setMapperCallback(null);
        }}
      />
    </div>
  );
}

// ── Sub-nav button ──
function SubNavBtn({ active, onClick, icon, label, disabled }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        active
          ? 'bg-primary text-primary-foreground shadow-sm'
          : disabled
          ? 'text-muted-foreground opacity-50 cursor-not-allowed'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {icon} {label}
    </button>
  );
}

